import { AxiosError } from "axios";
import { useState } from "react";
import { useDispatch } from "react-redux";

import axioInstance from "@/libs/axios";
import { setApiError } from "@/store/slices/errors/api-error.slice";
import { QuoteData } from "@/types/quote/quote-data";
import { apiErrorMappings } from "@/utils/errors/api-error-map";
import { trimFormValue } from "@/utils/form-input.utils";
import { removeAllSpaces } from "@/utils/review.utils";

type SubmitData = {
  type: string;
  channel: string;
  version: string;
  attributes: {
    domain?: string;
    paymentDetails: {
      discounts: {
        promoCode: {
          code: string;
        };
        voucher: SubmitVoucherCode[];
      };
    };
  };
};

export type SubmitVoucherCode = {
  index: number;
  code: string;
};

export const useSubmitCodes = (
  {
    version,
    type,
    attributes,
    referenceId,
  }: {
    version: string | undefined;
    type: string;
    attributes: QuoteData["attributes"];
    referenceId: string | undefined;
  },
  promoCode: string,
  voucherCode: SubmitVoucherCode[],
  setButtonDisabled: (boolean: boolean) => void,
  setIsLoading?: (boolean: boolean) => void,
  promoCodeTrimEnabled?: boolean
) => {
  const [newQuote, setNewQuote] = useState<QuoteData>();
  const [errorApi, setErrorApi] = useState<AxiosError>();
  const dispatch = useDispatch();

  const submitCodes: () => Promise<void> = async () => {
    setButtonDisabled(true);
    if (setIsLoading) {
      setIsLoading(true);
    }
    try {
      if (version) {
        const requestBody: SubmitData = {
          type,
          channel: "ONLINE",
          version,
          attributes: {
            domain: attributes.domain,
            paymentDetails: {
              discounts: {
                promoCode: {
                  code: promoCodeTrimEnabled
                    ? removeAllSpaces(promoCode)
                    : trimFormValue(promoCode),
                },
                voucher: voucherCode.map((v) => ({
                  ...v,
                  code: trimFormValue(v.code),
                })),
              },
            },
          },
        };

        const config = {
          data: requestBody,
        };
        const response = await axioInstance.patch(
          `/quote/${referenceId}/applyDiscount`,
          config
        );
        setNewQuote(response.data.data);
      }
    } catch (error) {
      const axiosError = error as AxiosError;
      setErrorApi(axiosError);
      if (axiosError.response?.status === 401) {
        window.parent.location.href = "/";
      } else {
        dispatch(setApiError(await apiErrorMappings(axiosError)));
      }
    } finally {
      setButtonDisabled(false);
      if (setIsLoading) {
        setIsLoading(false);
      }
    }
  };

  return [newQuote, submitCodes, errorApi];
};
